/**
 * Dôme de vapeur (author's map, not in the GDD): a circuit of ice, water and steam. Three hot pools, each over a pocket
 * of lava behind a plate of obsidian, boil (MapState.vents); the steam rises under a roof of ice, which catches it and
 * lets it fall back as rain (sim: steam + ice); the rain runs down the hills into the pools. The pools are shallow: one
 * wades across. Rock pillars stand on the hills and hold the roof, a tunnel goes through each hill under its pillar;
 * slits at the low points of the roof let the rain on top back in, and each end bay has a gap one can drop through.
 * Drawn: the seed changes nothing here.
 */
import { Physique, sinDeg, TRIG_ONE } from '@invaders/sim';
import { plainMap, type Box, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rect, sea, settle } from './common';

const { EMPTY, ROCK, SAND, WATER, LAVA, OBSIDIAN, ICE } = Physique;

/** Centre columns of the pools, and their half width with the slopes. */
export const DOME_POOLS = [271, 512, 753];
const POOL_HALF = 62;
const POOL_SLOPE = 14;
/** Heights above the sea: the floor of a pool, the rim, the top of its water (DOME_DEPTH cells: head above water). */
const POOL_FLOOR = 50;
const POOL_RIM = 66;
/** The hill under a pillar: its height, how far it reaches; the tunnel through it (floor height, rows). */
const HILL_TOP = 118;
const HILL_REACH = 58;
const TUNNEL_FLOOR = 72;
const TUNNEL_ROWS = 24;
export const DOME_DEPTH = 12;
/** Columns of the pillars, and of the towers at both ends. */
const PILLARS = [392, 632];
const TOWERS = [150, 874];
const SPRING_ROW = 250;
const ROOF_RISE = 54;
const ROOF_THICK = 9;
/** Open at the low points of the roof: a slit for the rain, a gap for the invaders at both ends of the dome. */
const ROOF_SLIT = 8;
const ROOF_GAP = 22;

export function drawDome(g: Gen): MapState {
  const { seaY, w } = g;
  const nearest = (x: number) => Math.min(...DOME_POOLS.map((c) => Math.abs(x - c)));
  const supports = [...PILLARS, ...TOWERS];
  const nearestSupport = (x: number) => Math.min(...supports.map((p) => Math.abs(x - p)));
  const hill = (x: number) => Math.min(HILL_TOP, POOL_RIM + Math.trunc((Math.max(0, HILL_REACH - nearestSupport(x)) * 5) / 3));
  const elevation = (x: number) => {
    const d = nearest(x);
    if (d <= POOL_HALF - POOL_SLOPE) return POOL_FLOOR;
    if (d < POOL_HALF) return POOL_FLOOR + Math.trunc(((d - POOL_HALF + POOL_SLOPE) * (POOL_RIM - POOL_FLOOR)) / POOL_SLOPE);
    return Math.max(POOL_RIM, hill(x));
  };
  const surface = island(g, 60, 964, 60, elevation);

  // The pools: lava under an obsidian plate, then the water.
  const waterRow = seaY - POOL_FLOOR - DOME_DEPTH;
  const vents: Box[] = [];
  for (const c of DOME_POOLS) {
    const half = POOL_HALF - POOL_SLOPE;
    const floor = seaY - POOL_FLOOR;
    ellipse(g, c, floor + 15, half - 4, 9, LAVA, ROCK);
    rect(g, c - half, floor, c + half, floor + 3, OBSIDIAN);
    for (let x = c - POOL_HALF; x <= c + POOL_HALF; x++) rect(g, x, waterRow, x, surface[x] - 1, WATER, EMPTY);
    vents.push({ x0: c - half, y0: waterRow, x1: c + half, y1: floor - 1 });
  }

  // Pillars and end towers of rock on their hills, a tunnel through each hill.
  for (const x of supports) {
    const half = TOWERS.includes(x) ? 14 : 10;
    rect(g, x - half, SPRING_ROW - 12, x + half, surface[x] - 1, ROCK);
    rect(g, x - half - 4, SPRING_ROW - 16, x + half + 4, SPRING_ROW - 12, ROCK);
    rect(g, x - HILL_REACH, seaY - TUNNEL_FLOOR - TUNNEL_ROWS, x + HILL_REACH, seaY - TUNNEL_FLOOR - 1, EMPTY);
  }

  // The roof: one arch of ice per bay, stalactites under it.
  const ends = [TOWERS[0] + 14, PILLARS[0] - 10, PILLARS[0] + 10, PILLARS[1] - 10, PILLARS[1] + 10, TOWERS[1] - 14];
  for (let k = 0; k < ends.length; k += 2) {
    const [a, b] = [ends[k], ends[k + 1]];
    const row = (x: number) => SPRING_ROW - Math.trunc((ROOF_RISE * sinDeg(Math.trunc(((x - a) * 180) / (b - a)))) / TRIG_ONE);
    const [open0, open1] = [k === 0 ? ROOF_GAP : ROOF_SLIT, k === ends.length - 2 ? ROOF_GAP : ROOF_SLIT];
    for (let x = a + open0; x <= b - open1; x++) {
      rect(g, x, row(x), x, row(x) + ROOF_THICK, ICE);
      if ((x - a) % 24 !== 0) continue;
      for (let dy = 0; dy < 12; dy++) rect(g, x - Math.trunc((11 - dy) / 3), row(x) + ROOF_THICK + dy, x + Math.trunc((11 - dy) / 3), row(x) + ROOF_THICK + dy, ICE);
    }
  }

  layer(g, surface, 20, 130, (x) => Math.max(0, 5 - Math.trunc(Math.abs(x - 80) / 12)), SAND);
  layer(g, surface, 894, w - 20, (x) => Math.max(0, 5 - Math.trunc(Math.abs(x - 944) / 12)), SAND);
  sea(g);
  settle(g);
  return { ...plainMap('dome'), vents };
}
